import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import AdBanner from "@/components/feature/AdBanner";
import { useSearch } from "@/hooks/useSearch";
import { useAnalytics } from "@/hooks/useAnalytics";

const categories = [
  "Todas",
  "BrasileirÃ£o",
  "Internacional",
  "Mercado da Bola",
  "Entrevista",
  "SeleÃ§Ã£o",
  "Institucional",
  "Categorias de Base",
  "Copa do Brasil",
  "Sul-Americana",
];

const typeFilters = [
  { label: "Todas", value: "Todas" },
  { label: "NotÃ­cias Publicadas", value: "Publicadas" },
  { label: "NotÃ­cias Importadas", value: "Importadas" },
];

export default function SearchPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialQuery = searchParams.get("q") || "";
  const [inputValue, setInputValue] = useState(initialQuery);
  const [hasSearched, setHasSearched] = useState(!!initialQuery);
  const searchInputRef = useRef<HTMLInputElement>(null);
  useAnalytics("/busca");

  const {
    results,
    loading,
    error,
    totalCount,
    searchTerm,
    setSearchTerm,
    activeFilter,
    setActiveFilter,
    activeType,
    setActiveType,
    currentPage,
    setCurrentPage,
    paginatedResults,
    totalPages,
    performSearch,
  } = useSearch();

  // Run search on mount if query is present
  useEffect(() => {
    if (initialQuery && initialQuery !== searchTerm) {
      setSearchTerm(initialQuery);
      performSearch(initialQuery);
      setHasSearched(true);
    }
  }, []);

  // Update URL when search changes
  useEffect(() => {
    if (searchTerm) {
      const params = new URLSearchParams();
      params.set("q", searchTerm);
      window.history.replaceState({}, "", `${window.location.pathname}?${params.toString()}`);
    }
  }, [searchTerm]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputValue.trim()) return;
    setSearchTerm(inputValue.trim());
    performSearch(inputValue.trim());
    setHasSearched(true);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSubmit(e as unknown as React.FormEvent);
    }
  };

  const truncateText = (text: string | null, maxLength: number) => {
    if (!text) return "";
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  const highlightMatch = (text: string | null, term: string) => {
    if (!text || !term) return text || "";
    const regex = new RegExp(`(${term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "gi");
    const parts = text.split(regex);
    return parts.map((part, i) =>
      regex.test(part) ? (
        <mark key={i} className="bg-alvinegro-black/20 text-alvinegro-black font-semibold rounded px-0.5">
          {part}
        </mark>
      ) : (
        <span key={i}>{part}</span>
      )
    );
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <div className="absolute top-0 left-0 right-0 h-48 md:h-56 bg-gradient-to-b from-alvinegro-black to-white z-0" />

      <main className="pt-20 md:pt-24 pb-12 relative z-10">
        <div className="w-full px-4 md:px-8">
          {/* Breadcrumb */}
          <Breadcrumb
            items={[
              { label: "InÃ­cio", path: "/" },
              { label: "Busca" },
            ]}
          />

          {/* Header */}
          <div className="mb-8">
            <h1 className="font-serif font-bold text-3xl md:text-4xl text-alvinegro-black mb-2">
              Busca
            </h1>
            <p className="text-alvinegro-muted text-base">
              Pesquise notÃ­cias, artigos e clipagens sobre o Glorioso
            </p>
          </div>

          {/* Search Box */}
          <form onSubmit={handleSubmit} className="mb-8">
            <div className="flex items-center gap-3 bg-white border border-alvinegro-border rounded-2xl px-4 py-3 shadow-sm focus-within:border-alvinegro-black focus-within:shadow-md transition-all">
              <div className="w-10 h-10 flex items-center justify-center text-alvinegro-muted">
                <i className="ri-search-line text-xl"></i>
              </div>
              <input
                ref={searchInputRef}
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Buscar por tÃ­tulo, conteÃºdo, tags ou categoria..."
                className="flex-1 text-base text-alvinegro-black placeholder-alvinegro-muted focus:outline-none bg-transparent"
                autoFocus
              />
              {inputValue && (
                <button
                  type="button"
                  onClick={() => {
                    setInputValue("");
                    searchInputRef.current?.focus();
                  }}
                  className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-alvinegro-light transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-alvinegro-muted"></i>
                </button>
              )}
              <button
                type="submit"
                className="bg-alvinegro-black text-white px-5 py-2.5 rounded-xl text-sm font-medium hover:bg-alvinegro-dark transition-colors whitespace-nowrap cursor-pointer"
              >
                Buscar
              </button>
            </div>
          </form>

          {/* Search Info */}
          {hasSearched && searchTerm && (
            <div className="mb-6 flex items-center justify-between">
              <div>
                <span className="text-sm text-alvinegro-muted">
                  {loading ? (
                    "Buscando..."
                  ) : (
                    <>
                      <span className="font-semibold text-alvinegro-black">{totalCount}</span> resultados para{" "}
                      <span className="font-semibold text-alvinegro-black">&ldquo;{searchTerm}&rdquo;</span>
                    </>
                  )}
                </span>
              </div>
              {totalCount > 0 && (
                <div className="text-sm text-alvinegro-muted">
                  PÃ¡gina {currentPage} de {totalPages}
                </div>
              )}
            </div>
          )}

          {/* Filters */}
          {hasSearched && totalCount > 0 && (
            <div className="mb-6 space-y-4">
              {/* Type Filter */}
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm text-alvinegro-muted mr-2">Tipo:</span>
                {typeFilters.map((t) => (
                  <button
                    key={t.value}
                    onClick={() => {
                      setActiveType(t.value);
                      setCurrentPage(1);
                    }}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap cursor-pointer ${
                      activeType === t.value
                        ? "bg-alvinegro-black text-white"
                        : "bg-alvinegro-light text-alvinegro-dark hover:bg-alvinegro-border"
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>

              {/* Category Filter */}
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm text-alvinegro-muted mr-2">Categoria:</span>
                {categories.map((cat) => (
                  <button
                    key={cat}
                    onClick={() => {
                      setActiveFilter(cat);
                      setCurrentPage(1);
                    }}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap cursor-pointer ${
                      activeFilter === cat
                        ? "bg-alvinegro-black text-white"
                        : "bg-alvinegro-light text-alvinegro-dark hover:bg-alvinegro-border"
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Results */}
          {loading ? (
            <div className="space-y-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="flex gap-4 p-4 rounded-xl bg-alvinegro-light animate-pulse">
                  <div className="w-32 h-24 bg-alvinegro-border rounded-lg flex-shrink-0"></div>
                  <div className="flex-1 space-y-3">
                    <div className="h-5 bg-alvinegro-border rounded w-full"></div>
                    <div className="h-4 bg-alvinegro-border rounded w-3/4"></div>
                    <div className="h-3 bg-alvinegro-border rounded w-1/2"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
                <i className="ri-error-warning-line text-2xl text-red-600"></i>
              </div>
              <p className="text-red-600 font-medium">{error}</p>
              <p className="text-sm text-alvinegro-muted mt-1">Tente refazer sua busca.</p>
            </div>
          ) : hasSearched && totalCount === 0 ? (
            <div className="text-center py-16">
              <div className="w-20 h-20 rounded-full bg-alvinegro-light flex items-center justify-center mx-auto mb-4">
                <i className="ri-search-line text-3xl text-alvinegro-muted"></i>
              </div>
              <h3 className="text-lg font-semibold text-alvinegro-black mb-2">
                Nenhum resultado encontrado
              </h3>
              <p className="text-sm text-alvinegro-muted max-w-md mx-auto">
                NÃ£o encontramos resultados para &ldquo;{searchTerm}&rdquo;. Tente buscar com termos diferentes ou mais genÃ©ricos.
              </p>
              <div className="mt-6 flex items-center justify-center gap-2 flex-wrap">
                <span className="text-sm text-alvinegro-muted">SugestÃµes:</span>
                {["Botafogo", "Textor", "Internacional", "Arthur Cabral", "BrasileirÃ£o"].map((s) => (
                  <button
                    key={s}
                    onClick={() => {
                      setInputValue(s);
                      setSearchTerm(s);
                      performSearch(s);
                      setHasSearched(true);
                    }}
                    className="text-sm text-alvinegro-black font-medium bg-alvinegro-light px-3 py-1 rounded-full hover:bg-alvinegro-border transition-colors cursor-pointer"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          ) : hasSearched && totalCount > 0 ? (
            <div className="space-y-4">
              {paginatedResults.map((item) => (
                <div
                  key={item.id}
                  className="group flex flex-col sm:flex-row gap-4 p-4 rounded-xl bg-alvinegro-light hover:bg-alvinegro-border/50 transition-colors cursor-pointer"
                  onClick={() => {
                    if (item.type === "published" && item.slug) {
                      navigate(`/noticias/${item.slug}`);
                    } else if (item.source_url) {
                      window.open(item.source_url, "_blank", "noopener,noreferrer");
                    }
                  }}
                >
                  {/* Image */}
                  <div className="w-full sm:w-40 h-28 sm:h-24 flex-shrink-0 rounded-lg overflow-hidden bg-alvinegro-border">
                    <img
                      src={
                        item.image_url ||
                        "https://readdy.ai/api/search-image?query=Football%20match%20action%20professional%20sports%20photography%20dramatic&width=400&height=300&seq=search&orientation=landscape"
                      }
                      alt={item.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className="text-xs font-medium text-alvinegro-muted bg-white px-2 py-0.5 rounded-full">
                        {item.category || "Geral"}
                      </span>
                      <span className="text-xs text-alvinegro-muted">
                        {item.type === "published" ? (
                          <span className="flex items-center gap-1">
                            <i className="ri-article-line text-xs"></i>
                            NotÃ­cia
                          </span>
                        ) : (
                          <span className="flex items-center gap-1">
                            <i className="ri-rss-line text-xs"></i>
                            Importada
                          </span>
                        )}
                      </span>
                      {item.source && (
                        <span className="text-xs text-alvinegro-muted">
                          via {item.source}
                        </span>
                      )}
                    </div>

                    <h3 className="font-semibold text-base text-alvinegro-black leading-snug line-clamp-2 group-hover:text-alvinegro-gray transition-colors">
                      {highlightMatch(item.title, searchTerm)}
                    </h3>

                    <p className="text-sm text-alvinegro-muted mt-1 line-clamp-2">
                      {highlightMatch(
                        truncateText(item.subtitle || item.summary || item.content, 160),
                        searchTerm
                      )}
                    </p>

                    <div className="flex items-center gap-3 mt-2 text-xs text-alvinegro-muted">
                      <span>
                        {item.published_at
                          ? new Date(item.published_at).toLocaleDateString("pt-BR", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                            })
                          : "Data nÃ£o disponÃ­vel"}
                      </span>
                      {item.tags && item.tags.length > 0 && (
                        <>
                          <span>â€¢</span>
                          <span className="flex items-center gap-1 flex-wrap">
                            {item.tags.slice(0, 3).map((tag) => (
                              <span key={tag} className="bg-white px-1.5 py-0.5 rounded text-xs">
                                {tag}
                              </span>
                            ))}
                          </span>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Arrow */}
                  <div className="hidden sm:flex items-center justify-center w-10 h-10 flex-shrink-0 text-alvinegro-muted group-hover:text-alvinegro-black transition-colors">
                    <i className="ri-arrow-right-line text-lg"></i>
                  </div>
                </div>
              ))}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-8">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="w-10 h-10 rounded-full flex items-center justify-center bg-alvinegro-light text-alvinegro-dark hover:bg-alvinegro-border disabled:opacity-50 transition-colors cursor-pointer"
                  >
                    <i className="ri-arrow-left-s-line"></i>
                  </button>
                  {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                    let page: number;
                    if (totalPages <= 7) {
                      page = i + 1;
                    } else if (currentPage <= 4) {
                      page = i + 1;
                    } else if (currentPage >= totalPages - 3) {
                      page = totalPages - 6 + i;
                    } else {
                      page = currentPage - 3 + i;
                    }
                    return (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium transition-colors cursor-pointer ${
                          currentPage === page
                            ? "bg-alvinegro-black text-white"
                            : "bg-alvinegro-light text-alvinegro-dark hover:bg-alvinegro-border"
                        }`}
                      >
                        {page}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="w-10 h-10 rounded-full flex items-center justify-center bg-alvinegro-light text-alvinegro-dark hover:bg-alvinegro-border disabled:opacity-50 transition-colors cursor-pointer"
                  >
                    <i className="ri-arrow-right-s-line"></i>
                  </button>
                </div>
              )}
            </div>
          ) : (
            /* Initial state - no search yet */
            <div className="text-center py-16">
              <div className="w-20 h-20 rounded-full bg-alvinegro-light flex items-center justify-center mx-auto mb-4">
                <i className="ri-search-line text-3xl text-alvinegro-muted"></i>
              </div>
              <h3 className="text-lg font-semibold text-alvinegro-black mb-2">
                Busque no Central Alvinegra
              </h3>
              <p className="text-sm text-alvinegro-muted max-w-md mx-auto">
                Digite um termo acima para buscar notÃ­cias, clipagens, artigos e conteÃºdo sobre o Botafogo.
              </p>
              <div className="mt-6 flex items-center justify-center gap-2 flex-wrap">
                <span className="text-sm text-alvinegro-muted">Busques populares:</span>
                {["Botafogo", "Textor", "Internacional", "Arthur Cabral", "BrasileirÃ£o", "Mercado da Bola"].map(
                  (s) => (
                    <button
                      key={s}
                      onClick={() => {
                        setInputValue(s);
                        setSearchTerm(s);
                        performSearch(s);
                        setHasSearched(true);
                      }}
                      className="text-sm text-alvinegro-black font-medium bg-alvinegro-light px-3 py-1 rounded-full hover:bg-alvinegro-border transition-colors cursor-pointer"
                    >
                      {s}
                    </button>
                  )
                )}
              </div>
            </div>
          )}

          {/* Ad Banner */}
          <div className="mt-8">
            <AdBanner variant="leaderboard" label="Publicidade" />
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
